import React, { useState } from 'react';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { ChevronDown, Check } from 'lucide-react';

export const PARTNERSHIP_TYPES = [
  'Member Offer',
  'Hangout Sponsorship',
  'DINK Weekend',
  'Community Activation',
  'Something else',
] as const;

type Status = 'idle' | 'submitting' | 'success' | 'error';

const inputClasses =
  'w-full rounded-xl border border-ink/15 bg-white px-4 py-3 text-[16px] text-ink placeholder:text-ink/40 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors';

const labelClasses =
  'block text-[11px] uppercase tracking-[0.22em] font-medium text-ink/60 mb-2';

const PartnerInquiryForm: React.FC = () => {
  const [status, setStatus] = useState<Status>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [partnershipType, setPartnershipType] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const payload = {
      name: data.get('name'),
      company: data.get('company'),
      email: data.get('email'),
      website: data.get('website'),
      partnershipType,
      message: data.get('message'),
      phone: data.get('phone'), // honeypot — hidden from real users
    };

    if (!partnershipType) {
      setErrorMessage('Please choose a partnership type.');
      setStatus('error');
      return;
    }

    setStatus('submitting');
    setErrorMessage('');
    try {
      const res = await fetch('/api/partner-inquiry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.ok) {
        throw new Error(
          body.error || 'Something went wrong sending your message.'
        );
      }
      setStatus('success');
    } catch (err) {
      setErrorMessage(
        err instanceof Error && err.message
          ? err.message
          : 'Something went wrong sending your message.'
      );
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className="border border-ink/10 rounded-3xl p-8 md:p-10 text-center">
        <p
          className="font-display font-medium text-ink text-[24px] md:text-[28px] mb-3"
          style={{ lineHeight: 1.15, letterSpacing: '-0.015em' }}
        >
          Thanks, we got it
        </p>
        <p className="text-[16px] md:text-[17px] text-ink/70" style={{ lineHeight: 1.55 }}>
          We read every inquiry and we'll reply to you directly by email, usually
          within a few days.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate={false}>
      {/* Honeypot — visually hidden, tabbed past, ignored by real users */}
      <div className="absolute w-px h-px overflow-hidden -m-px" aria-hidden="true">
        <label htmlFor="partner-phone">Phone</label>
        <input
          id="partner-phone"
          type="text"
          name="phone"
          tabIndex={-1}
          autoComplete="off"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
        <div>
          <label htmlFor="partner-name" className={labelClasses}>
            Name
          </label>
          <input
            id="partner-name"
            type="text"
            name="name"
            required
            maxLength={200}
            autoComplete="name"
            className={inputClasses}
          />
        </div>
        <div>
          <label htmlFor="partner-company" className={labelClasses}>
            Company
          </label>
          <input
            id="partner-company"
            type="text"
            name="company"
            maxLength={200}
            autoComplete="organization"
            className={inputClasses}
          />
        </div>
        <div>
          <label htmlFor="partner-email" className={labelClasses}>
            Email
          </label>
          <input
            id="partner-email"
            type="email"
            name="email"
            required
            maxLength={254}
            autoComplete="email"
            className={inputClasses}
          />
        </div>
        <div>
          <label htmlFor="partner-website" className={labelClasses}>
            Website
          </label>
          <input
            id="partner-website"
            type="text"
            name="website"
            maxLength={300}
            autoComplete="url"
            placeholder="yourbrand.com"
            className={inputClasses}
          />
        </div>
      </div>

      <div className="mb-5">
        <span id="partner-type-label" className={labelClasses}>
          Partnership type
        </span>
        <Listbox value={partnershipType} onChange={setPartnershipType}>
          <div className="relative">
            <ListboxButton
              aria-labelledby="partner-type-label"
              className={`${inputClasses} relative text-left pr-10`}
            >
              <span className={partnershipType ? '' : 'text-ink/40'}>
                {partnershipType || 'Select one'}
              </span>
              <ChevronDown
                className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 h-4 w-4 text-ink/50"
                strokeWidth={2}
                aria-hidden="true"
              />
            </ListboxButton>
            <ListboxOptions className="absolute z-10 mt-2 w-full rounded-xl border border-ink/10 bg-white p-1.5 shadow-lg shadow-ink/5 focus:outline-none">
              {PARTNERSHIP_TYPES.map((t) => (
                <ListboxOption
                  key={t}
                  value={t}
                  className="flex cursor-pointer items-center justify-between rounded-lg px-3.5 py-2.5 text-[16px] text-ink data-[focus]:bg-primary/5 data-[selected]:font-medium data-[selected]:text-primary"
                >
                  {({ selected }) => (
                    <>
                      {t}
                      {selected && (
                        <Check className="h-4 w-4" strokeWidth={2.5} aria-hidden="true" />
                      )}
                    </>
                  )}
                </ListboxOption>
              ))}
            </ListboxOptions>
          </div>
        </Listbox>
      </div>

      <div className="mb-7">
        <label htmlFor="partner-message" className={labelClasses}>
          Message
        </label>
        <textarea
          id="partner-message"
          name="message"
          required
          rows={5}
          maxLength={5000}
          className={inputClasses}
        />
      </div>

      {status === 'error' && (
        <div className="border-l-2 border-primary pl-5 py-1 mb-6" role="alert">
          <p className="font-display font-medium text-ink text-[16px] mb-1">
            Your message didn't go through
          </p>
          <p className="text-[15px] text-ink/70" style={{ lineHeight: 1.55 }}>
            {errorMessage} You can also email us at{' '}
            <a
              href="mailto:partnerships@dinksocial.com"
              className="text-primary font-medium hover:text-primary-dark hover:underline"
            >
              partnerships@dinksocial.com
            </a>
            .
          </p>
        </div>
      )}

      <button
        type="submit"
        disabled={status === 'submitting'}
        className="text-base font-semibold px-10 py-4 rounded-full bg-primary text-white hover:bg-primary-dark transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {status === 'submitting' ? 'Sending…' : 'Send inquiry'}
      </button>
    </form>
  );
};

export default PartnerInquiryForm;
